// src/hooks/useFeedbacks.ts
import { useState, useEffect } from 'react';
import { message } from 'antd';
import { feedbacksApi, Feedback, FeedbackStats } from '@/services/api/feedbacks';

interface FeedbacksParams {
  limit?: number;
  offset?: number;
  rating?: number;
  status?: string;
  is_approved?: 'pending' | 'approved' | 'all';
  sort?: 'newest' | 'oldest' | 'highest' | 'lowest';
  search?: string;
}

export const useFeedbacks = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    has_more: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Загрузка списка отзывов
  const loadFeedbacks = async (params?: FeedbacksParams) => {
    setLoading(true);
    try {
      console.log('🔍 Загрузка отзывов с параметрами:', params);
      const response = await feedbacksApi.getFeedbacks(params);
      console.log('✅ Ответ от API:', response.data);
      const data = response.data.data;
      
      console.log('📊 Отзывы:', data.reviews);
      console.log('📈 Статистика:', data.stats);
      console.log('📄 Пагинация:', data.pagination);
      
      setFeedbacks(data.reviews);
      setStats(data.stats);
      setPagination(data.pagination);
    } catch (error: any) {
      console.error('❌ Ошибка загрузки отзывов:', error);
      console.error('❌ Ответ сервера:', error.response?.data);
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки отзывов';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Одобрить отзыв
  const approveFeedback = async (feedbackId: string) => {
    setActionLoading(true);
    try {
      await feedbacksApi.approveFeedback(feedbackId);
      message.success('Отзыв одобрен');
      
      // Обновляем локальное состояние
      setFeedbacks(prev => prev.map(feedback => 
        feedback.id === feedbackId 
          ? { ...feedback, is_approved: true }
          : feedback
      ));
      
      // Обновляем статистику
      if (stats) {
        setStats({
          ...stats,
          pending_moderation: stats.pending_moderation - 1,
          approved: stats.approved + 1,
        });
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка одобрения отзыва';
      message.error(errorMsg);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Отклонить отзыв
  const rejectFeedback = async (feedbackId: string) => {
    setActionLoading(true);
    try {
      await feedbacksApi.rejectFeedback(feedbackId);
      message.success('Отзыв отклонён');
      
      // Обновляем локальное состояние
      setFeedbacks(prev => prev.map(feedback => 
        feedback.id === feedbackId 
          ? { ...feedback, is_approved: false }
          : feedback
      ));
      
      // Обновляем статистику
      if (stats) {
        setStats({
          ...stats,
          pending_moderation: stats.pending_moderation - 1,
        });
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка отклонения отзыва';
      message.error(errorMsg);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Массовое одобрение
  const bulkApproveFeedbacks = async (feedbackIds: string[]) => {
    setActionLoading(true);
    try {
      const response = await feedbacksApi.bulkApproveFeedbacks(feedbackIds);
      message.success(response.data.data.message);
      
      // Обновляем локальное состояние
      setFeedbacks(prev => prev.map(feedback => 
        feedbackIds.includes(feedback.id)
          ? { ...feedback, is_approved: true }
          : feedback
      ));
      
      // Обновляем статистику
      if (stats) {
        setStats({
          ...stats,
          pending_moderation: stats.pending_moderation - feedbackIds.length,
          approved: stats.approved + feedbackIds.length,
        });
      }
      
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка массового одобрения';
      message.error(errorMsg);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Массовое отклонение
  const bulkRejectFeedbacks = async (feedbackIds: string[]) => {
    setActionLoading(true);
    try {
      const response = await feedbacksApi.bulkRejectFeedbacks(feedbackIds);
      message.success(response.data.data.message);
      
      // Обновляем локальное состояние
      setFeedbacks(prev => prev.map(feedback => 
        feedbackIds.includes(feedback.id)
          ? { ...feedback, is_approved: false }
          : feedback
      ));
      
      // Обновляем статистику
      if (stats) {
        setStats({
          ...stats,
          pending_moderation: stats.pending_moderation - feedbackIds.length,
        });
      }
      
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка массового отклонения';
      message.error(errorMsg);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Перезагрузка текущих данных
  const refresh = () => {
    loadFeedbacks({
      limit: pagination.limit,
      offset: pagination.offset,
    });
  };

  // Загрузка базовых данных при монтировании
  useEffect(() => {
    loadFeedbacks({ is_approved: 'all' }); // ИЗМЕНЕНО: загружаем все отзывы
  }, []);

  return {
    // Данные
    feedbacks,
    stats,
    pagination,
    
    // Состояние загрузки
    loading,
    actionLoading,
    
    // Методы
    loadFeedbacks,
    approveFeedback,
    rejectFeedback,
    bulkApproveFeedbacks,
    bulkRejectFeedbacks,
    refresh,
  };
};